import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash/debounce'

import type { DrawerWidthLayout } from './drawer-width-layout'

// Below this a pointer gesture counts as a click, not a drag, so a stray wobble
// can never nudge the width.
const DRAG_THRESHOLD_PX = 4
const KEYBOARD_STEP_PX = 16
// A keyboard step is instantaneous and has no release event, so its
// drawer:animation-end waits until the steps stop arriving. Long enough to
// absorb key auto-repeat, short enough that consumers settle promptly.
const KEYBOARD_MOTION_IDLE_MS = 300

// Drag-to-resize handle on a drawer edge in the new layout. Stimulus rather
// than Vue: the drawer and the handle are server-rendered slim, and the only
// state is this element's own gesture.
export default abstract class DrawerResizerController extends Controller<HTMLElement> {
  protected abstract readonly layout: DrawerWidthLayout
  // Page-wide drag mode: keeps the resize cursor, suppresses text selection, and
  // drops the layout's width transitions (see two-drawer-layout-new.scss) so the
  // drawer tracks the pointer 1:1.
  protected abstract readonly resizingClass: string
  protected abstract readonly widenKey: 'ArrowLeft' | 'ArrowRight'

  private dragging = false
  private dragMoved = false
  private startX = 0
  // Drawer width in pixels, mirrored to aria-valuenow: seeded from the rendered
  // layout, then tracked from the clamped value `setWidthPx` returns.
  private width: number | null = null
  // Geometry snapshot for the active drag, taken on pointerdown; null when the
  // drawer element is missing.
  private dragMeasure: ((clientX: number) => number) | null = null
  // The pointer that started the drag. Capture retargets only that pointer, and
  // these listeners sit on window, so without this a second touch point would
  // steer the resize or end it at its own position.
  private dragPointerId: number | undefined

  connect(): void {
    this.element.setAttribute('aria-valuemin', String(this.layout.minWidthPx))
    this.element.setAttribute('aria-valuemax', String(this.layout.maxWidthPx))
    this.syncWidth()

    window.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('pointercancel', this.onPointerCancel)
  }

  disconnect(): void {
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('pointercancel', this.onPointerCancel)

    this.setDragging(false)
    this.closeKeyboardMotion.flush()
  }

  startDrag(event: PointerEvent): void {
    if (event.button !== 0) return
    event.preventDefault()

    this.startX = event.clientX
    this.dragMoved = false
    this.dragMeasure = this.layout.beginDragMeasurement()
    this.dragPointerId = event.pointerId
    this.setDragging(true)

    // Capture guarantees pointerup/pointercancel delivery even when released
    // outside the window. The guard narrows the type before the call.
    if (event.pointerId !== undefined) {
      this.element.setPointerCapture(event.pointerId)
    }
  }

  // ARIA window-splitter keyboard pattern: arrows step the width, Home/End jump
  // to the bounds. Each step persists, because there is no drop to persist on.
  nudge(event: KeyboardEvent): void {
    if (this.width === null) return

    let target: number
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
        target =
          this.width +
          (event.key === this.widenKey ? KEYBOARD_STEP_PX : -KEYBOARD_STEP_PX)
        break
      case 'Home':
        target = this.layout.minWidthPx
        break
      case 'End':
        target = this.layout.maxWidthPx
        break
      default:
        return
    }

    event.preventDefault()
    const available = this.layout.maxAvailableWidthPx()
    if (available !== null) target = Math.min(target, available)
    this.applyWidth(target, { persist: true })
    this.announceKeyboardMotion()
  }

  // The rendered width can have moved since connect (a viewport resize crossing
  // a breakpoint), so re-read it as the baseline for the arrow keys.
  onFocus(): void {
    this.syncWidth()
  }

  private isDragPointer(event: PointerEvent): boolean {
    return this.dragging && event.pointerId === this.dragPointerId
  }

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.isDragPointer(event)) return
    if (Math.abs(event.clientX - this.startX) > DRAG_THRESHOLD_PX) {
      this.dragMoved = true
    }
    if (!this.dragMoved || !this.dragMeasure) return

    this.applyWidth(this.dragMeasure(event.clientX), { persist: false })
  }

  private onPointerUp = (event: PointerEvent): void => {
    if (!this.isDragPointer(event)) return

    // Land the final width while the transition is still suppressed. Lifting
    // the suppression first eases the last frame of travel, because pointerup
    // carries the true release position while pointermove is coalesced to the
    // frame rate.
    if (this.dragMoved && this.dragMeasure) {
      this.applyWidth(this.dragMeasure(event.clientX), { persist: true })
    }

    this.setDragging(false)
  }

  // A cancelled stream (e.g. touch gesture takeover) abandons the drag: the
  // live width stays as dragged, but nothing is persisted.
  private onPointerCancel = (event: PointerEvent): void => {
    if (!this.isDragPointer(event)) return
    this.setDragging(false)
  }

  // The handle's :focus-visible rule suppresses the transition, so a keyboard
  // step moves the drawer without firing transition events, exactly as a drag
  // does. Consumers that reposition on drawer motion need the pair either way.
  private keyboardMotionOpen = false

  private closeKeyboardMotion = debounce((): void => {
    if (!this.keyboardMotionOpen) return
    this.keyboardMotionOpen = false
    window.eventHub.$emit('drawer:animation-end')
  }, KEYBOARD_MOTION_IDLE_MS)

  private announceKeyboardMotion(): void {
    if (!this.keyboardMotionOpen) {
      this.keyboardMotionOpen = true
      window.eventHub.$emit('drawer:animation-start')
    }
    this.closeKeyboardMotion()
  }

  private applyWidth(px: number, options: { persist: boolean }): void {
    this.setWidth(this.layout.setWidthPx(px, options))
  }

  private syncWidth(): void {
    const width = this.layout.readWidthPx()
    if (width === null) return

    const requested = this.layout.requestedWidthPx()
    const available = this.layout.maxAvailableWidthPx()
    if (requested !== null && available !== null && requested > available) {
      this.applyWidth(available, { persist: false })
      return
    }

    this.setWidth(width)
  }

  // Clamped because on a page that loads collapsed the rendered width
  // `syncWidth` reads is the collapsed drawer's, which would announce an
  // aria-valuenow below the aria-valuemin set alongside it.
  private setWidth(px: number): void {
    this.width = Math.min(
      this.layout.maxWidthPx,
      Math.max(this.layout.minWidthPx, px)
    )
    this.element.setAttribute('aria-valuenow', String(this.width))
  }

  private setDragging(dragging: boolean): void {
    if (this.dragging === dragging) return

    this.dragging = dragging
    // Drives the handle's active styling, which cannot lean on :hover once the
    // pointer has travelled off the handle.
    this.element.dataset.dragging = String(dragging)
    document.body.classList.toggle(this.resizingClass, dragging)

    // A drag writes the width directly, so it fires no transition events.
    // Consumers that reposition on drawer motion need the pair announced here
    // or they hold coordinates taken before the drag.
    window.eventHub.$emit(
      dragging ? 'drawer:animation-start' : 'drawer:animation-end'
    )
  }
}
